import React, { createContext, useCallback, useContext, useEffect, useMemo, useReducer, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import type { LucideIcon } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { findRouteMeta, podeAbrirRota } from '@/app/registry/routes';
import { menuGroups } from '@/app/registry/menu';
import type { SubMenuItem } from '@/app/registry/menu';
import { getTabsStorageKey, MAX_TABS, trimTabsByPolicy } from './tabPolicy';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';

export type AppTab = {
  key: string;
  path: string;
  title: string;
  section: string;
  createdAt?: number;
  lastActiveAt?: number;
  iconName?: string;
  pinned?: boolean;
  closable?: boolean;
  dirty?: boolean;
  scrollTop?: number;
};

type TabsState = {
  tabs: AppTab[];
  activeKey: string | null;
};

type OpenTabInput = Omit<AppTab, 'closable'> & {
  closable?: boolean;
  icon?: LucideIcon;
};

type TabsContextValue = TabsState & {
  openTab: (tab: OpenTabInput) => void;
  closeTab: (key: string) => AppTab | null;
  /** Fecha a aba e vai para a vizinha; com alteração não salva, pede confirmação antes. */
  fecharAba: (key: string) => void;
  focusTab: (key: string) => void;
  updateTab: (key: string, patch: Partial<AppTab>) => void;
};

type Action =
  | { type: 'open'; tab: AppTab }
  | { type: 'close'; key: string }
  | { type: 'focus'; key: string }
  | { type: 'update'; key: string; patch: Partial<AppTab> }
  | { type: 'reset'; state: TabsState };

const initialState: TabsState = {
  tabs: [],
  activeKey: null,
};

const now = () => Date.now();

const coletarHrefs = (itens: SubMenuItem[] | undefined): string[] =>
  (itens ?? []).flatMap((item) => [item.href, ...coletarHrefs(item.subItems)]);

// Sem / e /dashboards: a aba fixa "Dashboards" salva no navegador some ao recarregar (03/11/2026).
const rotasDoMenu = new Set([...menuGroups.flatMap((group) => coletarHrefs(group.items))]);

const isRotaDoMenu = (path: string) => rotasDoMenu.has(path.split('?')[0].replace(/\/+$/, '') || '/');

const sanitizeTab = (tab: OpenTabInput): AppTab => {
  const timestamp = now();

  return {
    key: tab.key,
    path: tab.path,
    title: tab.title,
    section: tab.section,
    createdAt: tab.createdAt || timestamp,
    lastActiveAt: timestamp,
    iconName: tab.iconName || tab.icon?.displayName || tab.icon?.name,
    pinned: tab.pinned,
    closable: tab.closable ?? !tab.pinned,
    dirty: tab.dirty,
    scrollTop: tab.scrollTop,
  };
};

const reducer = (state: TabsState, action: Action): TabsState => {
  switch (action.type) {
    case 'open': {
      const existing = state.tabs.find((tab) => tab.key === action.tab.key);
      const tabs = existing
        ? state.tabs.map((tab) =>
            tab.key === action.tab.key
              // Reabrir a aba (troca de rota) não apaga a posição de rolagem guardada.
              ? { ...tab, ...action.tab, createdAt: tab.createdAt || action.tab.createdAt, scrollTop: action.tab.scrollTop ?? tab.scrollTop }
              : tab,
          )
        : trimTabsByPolicy([...state.tabs, action.tab], action.tab.key);

      return {
        tabs,
        activeKey: action.tab.key,
      };
    }
    case 'close': {
      const closingIndex = state.tabs.findIndex((tab) => tab.key === action.key);
      if (closingIndex === -1) return state;

      const closingTab = state.tabs[closingIndex];
      if (closingTab.pinned || closingTab.closable === false) return state;

      const tabs = state.tabs.filter((tab) => tab.key !== action.key);
      const fallbackTab = tabs[Math.max(0, closingIndex - 1)] || tabs[0] || null;

      return {
        tabs,
        activeKey: state.activeKey === action.key ? fallbackTab?.key ?? null : state.activeKey,
      };
    }
    case 'focus':
      return {
        ...state,
        activeKey: action.key,
        tabs: state.tabs.map((tab) =>
          tab.key === action.key ? { ...tab, lastActiveAt: now() } : tab,
        ),
      };
    case 'update':
      return {
        ...state,
        tabs: state.tabs.map((tab) => (tab.key === action.key ? { ...tab, ...action.patch } : tab)),
      };
    case 'reset':
      return action.state;
    default:
      return state;
  }
};

const readInitialState = (storageKey: string | null): TabsState => {
  if (typeof window === 'undefined' || !storageKey) return initialState;

  try {
    const parsed = JSON.parse(window.localStorage.getItem(storageKey) || '');
    if (!parsed || !Array.isArray(parsed.tabs)) return initialState;

    const tabs = parsed.tabs
      .filter((tab: AppTab) => tab?.key && tab?.path && tab?.title)
      // Abas salvas de rotinas que saíram do menu (ex.: Análise de Projetos) não voltam a abrir (28/10/2026).
      .filter((tab: AppTab) => isRotaDoMenu(tab.path))
      .slice(0, MAX_TABS)
      .map((tab: AppTab) => ({
        ...tab,
        // Título atual da rota: abas salvas antes guardavam o nome tirado da URL ("St").
        title: findRouteMeta(tab.path.split('?')[0])?.title || tab.title,
        // O formulário não sobrevive ao recarregar: a marca de alteração não salva também não.
        dirty: false,
        createdAt: tab.createdAt || now(),
        lastActiveAt: tab.lastActiveAt || tab.createdAt || now(),
      }));
    // A aba da URL atual já começa ativa, para não piscar a última aba salva antes de a rota abrir a dela.
    const keyDaUrl = window.location.pathname === '/' ? '/' : `${window.location.pathname}${window.location.search}`;
    const activeKey = tabs.some((tab: AppTab) => tab.key === keyDaUrl)
      ? keyDaUrl
      : typeof parsed.activeKey === 'string' && tabs.some((tab: AppTab) => tab.key === parsed.activeKey)
        ? parsed.activeKey
        : tabs[0]?.key ?? null;

    return {
      tabs,
      activeKey,
    };
  } catch {
    return initialState;
  }
};

const TabsContext = createContext<TabsContextValue | null>(null);

export const TabsProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user, hasPermission } = useAuth();
  const storageKey = useMemo(() => getTabsStorageKey(user?.id), [user?.id]);
  const [state, dispatch] = useReducer(reducer, storageKey, readInitialState);
  const skipNextPersistRef = useRef(false);
  const loadedStorageKeyRef = useRef(storageKey);

  useEffect(() => {
    // Na montagem o estado já veio desta chave (readInitialState). Recarregar aqui descartava a aba que a rota atual
    // acabou de abrir (o efeito do filho roda antes) e a tela ficava em branco ao dar F5 ou abrir um link (28/10/2026).
    if (loadedStorageKeyRef.current === storageKey) return;
    loadedStorageKeyRef.current = storageKey;
    skipNextPersistRef.current = true;
    dispatch({ type: 'reset', state: readInitialState(storageKey) });
  }, [storageKey]);

  useEffect(() => {
    if (!storageKey) return;
    if (skipNextPersistRef.current) {
      skipNextPersistRef.current = false;
      return;
    }
    window.localStorage.setItem(storageKey, JSON.stringify(state));
  }, [state, storageKey]);

  // Abas de telas que o usuário não pode abrir (salvas antes de perder a permissão) não aparecem.
  const permissoesKey = JSON.stringify(user?.permissoes ?? null);
  const tabsVisiveis = useMemo(
    () => state.tabs.filter((tab) => podeAbrirRota(tab.path.split('?')[0], hasPermission)),
    // hasPermission muda a cada render; o que importa são as permissões do usuário.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [state.tabs, permissoesKey],
  );

  const openTab = useCallback((tab: OpenTabInput) => {
    dispatch({ type: 'open', tab: sanitizeTab(tab) });
  }, []);

  const closeTab = useCallback(
    (key: string) => {
      const closingIndex = tabsVisiveis.findIndex((tab) => tab.key === key);
      const closingTab = tabsVisiveis[closingIndex];
      if (!closingTab || closingTab.pinned || closingTab.closable === false) return null;

      const remainingTabs = tabsVisiveis.filter((tab) => tab.key !== key);
      const fallbackTab = remainingTabs[Math.max(0, closingIndex - 1)] || remainingTabs[0] || null;
      dispatch({ type: 'close', key });
      return state.activeKey === key ? fallbackTab : null;
    },
    [state.activeKey, tabsVisiveis],
  );

  // Fechar com alteração não salva pede confirmação; antes o que foi digitado se perdia sem aviso (28/10/2026).
  const navigate = useNavigate();
  const [confirmarFechar, setConfirmarFechar] = useState<AppTab | null>(null);

  const fecharDeVez = useCallback(
    (key: string) => {
      const fallbackTab = closeTab(key);
      if (fallbackTab) navigate(fallbackTab.path);
    },
    [closeTab, navigate],
  );

  const fecharAba = useCallback(
    (key: string) => {
      const tab = tabsVisiveis.find((item) => item.key === key);
      if (tab?.dirty) {
        setConfirmarFechar(tab);
        return;
      }
      fecharDeVez(key);
    },
    [fecharDeVez, tabsVisiveis],
  );

  const temAlteracao = state.tabs.some((tab) => tab.dirty);
  useEffect(() => {
    if (!temAlteracao) return;
    // Recarregar ou fechar o navegador com formulário alterado: o navegador mostra a própria confirmação.
    const avisar = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      event.returnValue = '';
    };
    window.addEventListener('beforeunload', avisar);
    return () => window.removeEventListener('beforeunload', avisar);
  }, [temAlteracao]);

  const focusTab = useCallback((key: string) => {
    dispatch({ type: 'focus', key });
  }, []);

  const updateTab = useCallback((key: string, patch: Partial<AppTab>) => {
    dispatch({ type: 'update', key, patch });
  }, []);

  const value = useMemo<TabsContextValue>(
    () => ({
      ...state,
      tabs: tabsVisiveis,
      openTab,
      closeTab,
      fecharAba,
      focusTab,
      updateTab,
    }),
    [closeTab, fecharAba, focusTab, openTab, state, tabsVisiveis, updateTab],
  );

  return (
    <TabsContext.Provider value={value}>
      {children}
      <AlertDialog open={Boolean(confirmarFechar)} onOpenChange={(aberto) => !aberto && setConfirmarFechar(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Descartar alterações?</AlertDialogTitle>
            <AlertDialogDescription>
              Há um formulário com alterações não salvas em "{confirmarFechar?.title}". Se fechar a aba agora, o que foi
              digitado se perde.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Continuar editando</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              onClick={() => {
                if (confirmarFechar) fecharDeVez(confirmarFechar.key);
                setConfirmarFechar(null);
              }}
            >
              Descartar e fechar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </TabsContext.Provider>
  );
};

/** Igual a useTabs, mas devolve null fora do portal com abas (ex.: login), sem erro. */
export const useTabsOpcional = (): TabsContextValue | null => useContext(TabsContext);

export const useTabs = (): TabsContextValue => {
  const context = useContext(TabsContext);
  if (!context) {
    throw new Error('useTabs must be used within TabsProvider');
  }
  return context;
};
